import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { ShieldCheck, Heart, Zap, Globe, Eye, EyeOff, X, Play } from 'lucide-react';
import logo from '../../assets/Doxez.png';
import api from '../../services/api';

const Login = () => {
   const [formData, setFormData] = useState({
      email: '',
      password: ''
   });
   const [loading, setLoading] = useState(false);
   const { login } = useAuth();
   const navigate = useNavigate();
   const location = useLocation();
   const [showPassword, setShowPassword] = useState(false);
   const [showForgotModal, setShowForgotModal] = useState(false);
   const [forgotEmail, setForgotEmail] = useState('');
   const [forgotLoading, setForgotLoading] = useState(false);
   const [forgotSubmitted, setForgotSubmitted] = useState(false);
   const [showDemoVideo, setShowDemoVideo] = useState(false);
   const handleForgotSubmit = async (e) => {
      e.preventDefault();
      setForgotLoading(true);
      try {
         const response = await api.post('/auth/forgot-password', { email: forgotEmail });
         if (response.data.success) {
            setForgotSubmitted(true);
            toast.success('Password reset link sent to your email.');
         }
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to request password reset');
      } finally {
         setForgotLoading(false);
      }
   };

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value
      }));
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
         const result = await login(formData.email, formData.password);
         if (result.success) {
            toast.success('Access Granted. Workspace Unlocked.');
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
         } else {
            toast.error(result.message || 'Authentication Failed');
         }
      } catch (error) {
         toast.error('System Integrity Error: Authentication server unreachable');
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4 relative overflow-hidden font-sans">

         {/* Background Aesthetic Elements */}
         <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/10 rounded-full blur-[120px] -mr-96 -mt-96" />
         <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px] -ml-72 -mb-72" />

         <div className="w-full max-w-[1200px] grid grid-cols-1 lg:grid-cols-2 bg-white/5 backdrop-blur-2xl rounded-[40px] border border-white/10 shadow-[0_0_100px_rgba(0,0,0,0.3)] overflow-hidden animate-in fade-in zoom-in-95 duration-700">

            {/* Left Side: Branding & Trust */}
            <div className="hidden lg:flex flex-col justify-between p-16 bg-gradient-to-br from-[#143E78] to-[#0f172a] relative">
               <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-12">
                     <img src={logo} alt="Doxez" className="h-20 w-auto object-contain" />
                  </div>

                  <h1 className="text-3xl sm:text-4xl xl:text-[44px] font-black text-white leading-tight mb-6 italic tracking-tight">
                     Connected Healthcare.<br />
                     <span className="text-blue-400 underline decoration-blue-400/30 underline-offset-[10px] whitespace-nowrap inline-block mt-2">
                        Seamless Operations.
                     </span>
                  </h1>
                  <p className="text-blue-100/60 text-lg font-medium leading-relaxed max-w-md">
                     Streamline patient lifecycle tracking, partner coordination, and operational efficiency through a centralized healthcare system.
                  </p>

                  {/* <button 
                     type="button"
                     onClick={() => setShowDemoVideo(true)}
                     className="mt-8 flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded-full font-bold transition-all border border-white/20 hover:scale-105 active:scale-95 shadow-xl shadow-blue-900/20 w-fit"
                  >
                     <div className="bg-blue-500 rounded-full p-1">
                        <Play className="w-4 h-4 fill-white" />
                     </div>
                     Watch System Demo
                  </button> */}
               </div>

               {/* Decor */}
               <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
            </div>

            {/* Right Side: Login Form */}
            <div className="p-10 sm:p-20 flex flex-col justify-center bg-white">
               <div className="mb-10 text-center lg:text-left">
                  <div className="flex items-center gap-2 mb-2 justify-center lg:justify-start">
                     <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
                     <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Identity Gateway</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-[#143E78] tracking-tight mb-2">Welcome Back</h2>
                  <p className="text-gray-400 font-medium italic text-sm sm:text-base">Authenticate to access your surgical workspace.</p>
               </div>

               <form className="space-y-5 sm:space-y-6" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Enter Email</label>
                     <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-6 py-4 bg-gray-50 border-2 border-gray-50 rounded-2xl text-gray-900 font-bold focus:bg-white focus:border-blue-100 focus:outline-none transition-all placeholder:text-gray-300"
                        placeholder="name@doxez.com"
                     />
                  </div>

                  <div className="space-y-2">
                     <div className="flex items-center justify-between px-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Verification Password</label>
                        <button
                           type="button"
                           onClick={() => setShowForgotModal(true)}
                           className="text-[10px] font-black text-blue-600 uppercase hover:underline"
                        >
                           Forgot?
                        </button>
                     </div>
                     <div className="relative group/pass">
                        <input
                           type={showPassword ? "text" : "password"}
                           name="password"
                           required
                           value={formData.password}
                           onChange={handleChange}
                           className="w-full px-6 py-4 bg-gray-50 border-2 border-gray-50 rounded-2xl text-gray-900 font-bold focus:bg-white focus:border-blue-100 focus:outline-none transition-all placeholder:text-gray-300 pr-14"
                           placeholder="••••••••"
                        />
                        <button
                           type="button"
                           onClick={() => setShowPassword(!showPassword)}
                           className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#143E78] transition-colors"
                        >
                           {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                     </div>
                  </div>

                  <button
                     type="submit"
                     disabled={loading}
                     className="w-full bg-[#143E78] text-white py-4 sm:py-5 rounded-2xl font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-blue-900/20 hover:bg-[#0f2c56] active:scale-95 transition-all disabled:opacity-50 mt-4 flex items-center justify-center gap-3"
                  >
                     {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                     ) : (
                        <>Secure Login <ShieldCheck className="w-4 h-4" /></>
                     )}
                  </button>
               </form>

               <div className="mt-8 sm:mt-12 pt-8 border-t border-gray-100 text-center">
                  <p className="text-gray-400 text-xs sm:text-sm font-bold">
                     Encrypted & Protected by DOXEZ
                  </p>
               </div>
            </div>

         </div>

         {/* Footer Text */}
         <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center pointer-events-none">
            <p className="text-white/20 text-xs font-black uppercase tracking-[0.5em]">DOXEZ HEALTHTECH v2.0</p>
         </div>

         {/* Forgot Password Modal */}
         {showForgotModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
               {/* Backdrop */}
               <div
                  className="absolute inset-0 bg-[#0f172a]/60 backdrop-blur-sm"
                  onClick={() => {
                     setShowForgotModal(false);
                     setForgotEmail('');
                     setForgotSubmitted(false);
                  }}
               />

               {/* Modal Content */}
               <div className="bg-white rounded-[2rem] border border-slate-100 shadow-2xl w-full max-w-md p-8 sm:p-10 relative z-10 animate-in fade-in zoom-in-95 duration-200 text-slate-800">
                  <button
                     onClick={() => {
                        setShowForgotModal(false);
                        setForgotEmail('');
                        setForgotSubmitted(false);
                     }}
                     className="absolute right-6 top-6 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                     <X className="w-5 h-5" />
                  </button>

                  <div className="text-center mb-6">
                     <div className="flex items-center gap-2 mb-2 justify-center">
                        <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Security Center</span>
                     </div>
                     <h3 className="text-2xl font-black text-[#143E78] tracking-tight">Forgot Password</h3>
                     <p className="text-slate-500 text-xs font-bold italic mt-1">
                        {!forgotSubmitted
                           ? "Enter your registered email to receive a password reset link."
                           : "Instructions sent successfully."}
                     </p>
                  </div>

                  {!forgotSubmitted ? (
                     <form onSubmit={handleForgotSubmit} className="space-y-5">
                        <div className="space-y-2">
                           <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Email Address</label>
                           <input
                              type="email"
                              required
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                              className="w-full px-5 py-3.5 bg-slate-50 border-2 border-slate-100 focus:border-blue-200 focus:bg-white rounded-xl text-slate-900 font-bold outline-none transition-all placeholder:text-slate-300 text-sm"
                              placeholder="name@doxez.com"
                           />
                        </div>

                        <button
                           type="submit"
                           disabled={forgotLoading}
                           className="w-full bg-[#143E78] hover:bg-blue-800 text-white py-4 rounded-xl font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-blue-900/10 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                           {forgotLoading ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                           ) : (
                              <>Send Reset Link <ShieldCheck className="w-4 h-4" /></>
                           )}
                        </button>
                     </form>
                  ) : (
                     <div className="space-y-5">
                        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 text-center">
                           <p className="text-emerald-800 text-xs font-bold leading-relaxed">
                              If an account exists for <span className="underline">{forgotEmail}</span>, a secure password reset link has been sent to it. Please check your spam folder if it doesn't appear in a few minutes.
                           </p>
                        </div>
                        <button
                           onClick={() => {
                              setShowForgotModal(false);
                              setForgotEmail('');
                              setForgotSubmitted(false);
                           }}
                           className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 py-3.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all"
                        >
                           Close Window
                        </button>
                     </div>
                  )}
               </div>
            </div>
         )}

         {/* Demo Video Modal */}
         {false && showDemoVideo && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
               {/* Backdrop */}
               <div 
                  className="absolute inset-0 bg-[#0f172a]/90 backdrop-blur-md"
                  onClick={() => setShowDemoVideo(false)}
               />
               
               {/* Modal Content */}
               <div className="bg-black rounded-3xl border border-white/10 shadow-2xl w-full max-w-5xl relative z-10 animate-in fade-in zoom-in-95 duration-300 overflow-hidden flex flex-col">
                  
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 px-6 border-b border-white/10 bg-slate-900/50">
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                           <Play className="w-4 h-4 text-blue-400 fill-blue-400" />
                        </div>
                        <div>
                           <h3 className="text-white font-bold text-sm tracking-wide">DOXEZ System Workflow Demo</h3>
                           <p className="text-slate-400 text-xs">End-to-End Application Flow</p>
                        </div>
                     </div>
                     <button 
                        onClick={() => setShowDemoVideo(false)}
                        className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all"
                     >
                        <X className="w-5 h-5" />
                     </button>
                  </div>

                  {/* Video Player */}
                  <div className="aspect-video w-full bg-slate-950 flex items-center justify-center relative">
                     <video 
                        className="w-full h-full object-cover"
                        controls 
                        autoPlay 
                        name="media"
                     >
                        {/* Placeholder video source - replace with actual demo video when ready */}
                        <source src="https://www.w3schools.com/html/mov_bbb.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                     </video>
                  </div>
               </div>
            </div>
         )} 
      </div >
   );
};

export default Login;
